"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import { Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import type { SuggestRow } from "@/app/api/search/suggest/route";

/**
 * The ⌘K search palette — the "find or ask anything" entry as an overlay
 * instead of a page trip. Typing streams Meilisearch hits from
 * /api/search/suggest (debounced, aborted on every keystroke); Enter on the
 * default first row lands on /search?q=… (the full results page stays the
 * canonical destination and the no-JS fallback — the header icons are real
 * links to it), and the assistant escalation row mirrors the one on /search.
 * Opens via the header icons or the global `/` and Ctrl/⌘K shortcuts.
 *
 * Rendered only when the portal's ai_chat toggle is on — it rides the same
 * `ask` gate as the header entry it opens from.
 */
export function SearchModal({
  lang,
  isAuthed = false,
  open,
  onOpen,
  onClose,
}: {
  lang: Lang;
  /** /asistent is auth-gated — guests get the disclosed "sign in and ask"
   *  wording + a login-redirect href on the assistant row. */
  isAuthed?: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const router = useRouter();
  const t = useDict();

  // Global shortcuts: Ctrl/⌘K toggles, `/` opens (unless typing somewhere).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (open) onClose();
        else onOpen();
        return;
      }
      if (e.key === "/" && !open) {
        const el = e.target as HTMLElement | null;
        const editable =
          el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
        if (!editable) {
          e.preventDefault();
          onOpen();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpen, onClose]);

  function go(href: string | null) {
    if (!href) return;
    onClose();
    router.push(href);
  }

  return (
    // Named: without it a screen reader announced "dialog" and nothing else
    // (2026-09-14 review, confirmed in the accessibility tree).
    <Dialog open={open} onClose={onClose} aria-label={t.search.title} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition duration-150 ease-out data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 overflow-y-auto p-4 pt-[10vh] md:pt-[14vh]">
        <DialogPanel
          transition
          className="bg-card text-card-foreground border-border mx-auto w-full max-w-xl overflow-hidden rounded-2xl border transition duration-150 ease-out data-[closed]:-translate-y-2 data-[closed]:opacity-0"
          style={{ boxShadow: "var(--shadow-lift)" }}
        >
          {/* Palette state lives one level down so it mounts fresh per open —
              the Dialog unmounts its children when closed, which IS the reset. */}
          <Palette lang={lang} isAuthed={isAuthed} onSelect={go} />
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function Palette({
  lang,
  isAuthed,
  onSelect,
}: {
  lang: Lang;
  isAuthed: boolean;
  onSelect: (href: string | null) => void;
}) {
  const t = useDict();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SuggestRow[]>([]);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const query = q.trim();

  function onQueryChange(value: string) {
    setQ(value);
    // Whitespace does not start a new search or leave a completed one pending.
    if (value.trim() === query) return;
    requestRef.current?.abort();
    setFailed(false);
    // Keep whatever is on screen while the next query is in flight — clearing
    // per keystroke blanks the list for the debounce plus a round trip, so the
    // palette flickers empty for most of the typing. Rows are only dropped when
    // the box falls below the search threshold and nothing can be in flight.
    if (value.trim().length < 2) {
      setHits([]);
      setPending(false);
    } else {
      setPending(true);
    }
  }

  function retry() {
    requestRef.current?.abort();
    setFailed(false);
    setPending(true);
    setAttempt((n) => n + 1);
    inputRef.current?.focus({ preventScroll: true });
  }

  useEffect(() => {
    if (query.length < 2) return;
    const ctrl = new AbortController();
    requestRef.current = ctrl;
    let requestTimeout: ReturnType<typeof setTimeout> | undefined;
    const tm = setTimeout(async () => {
      // A stalled connection must recover just like an HTTP or offline error.
      requestTimeout = setTimeout(() => {
        if (ctrl.signal.aborted) return;
        setFailed(true);
        setPending(false);
        ctrl.abort();
      }, 10_000);
      try {
        const res = await fetch(`/api/search/suggest?q=${encodeURIComponent(query)}&lang=${lang}`, {
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error("Suggestion request failed");
        const body = (await res.json()) as { results?: SuggestRow[] };
        if (!Array.isArray(body.results)) throw new Error("Invalid suggestion response");
        if (!ctrl.signal.aborted) setHits(body.results);
      } catch {
        if (!ctrl.signal.aborted) setFailed(true);
      } finally {
        clearTimeout(requestTimeout);
        // An older request cannot clear the newer query's loading indicator.
        if (!ctrl.signal.aborted) setPending(false);
      }
    }, 200);
    return () => {
      ctrl.abort();
      clearTimeout(tm);
      clearTimeout(requestTimeout);
      if (requestRef.current === ctrl) requestRef.current = null;
    };
  }, [query, lang, attempt]);

  const askHref = `${langPath(lang, "/asistent")}?q=${encodeURIComponent(query)}`;
  const typeLabels: Record<string, string> = {
    course: t.detail.typeCourse,
    event: t.detail.typeEvent,
    ott: t.detail.typeVideo,
    podcast: t.detail.typePodcast,
    post: t.posts.articleLabel,
  };

  return (
    <Combobox onChange={onSelect}>
      <div className="border-border relative flex items-center gap-3 border-b px-5">
        <Search className="text-mid-gray size-5 shrink-0" aria-hidden />
        {/* focus-visible:shadow-none: globals.css paints a box-shadow ring on
            every :focus-visible element — right for buttons/links, but the
            palette's autofocused input would wear a hard blue rectangle inside
            an already-focused surface. */}
        <ComboboxInput
          ref={inputRef}
          value={q}
          autoFocus
          placeholder={t.assistant.findAsk}
          aria-label={t.assistant.findAsk}
          onChange={(e) => onQueryChange(e.target.value)}
          className="placeholder:text-mid-gray w-full bg-transparent py-4 text-base outline-none focus-visible:shadow-none"
        />
        {pending ? (
          <span
            aria-hidden
            className="bg-gold/80 absolute inset-x-0 -bottom-px h-0.5 animate-pulse"
          />
        ) : null}
      </div>
      {query ? (
        <ComboboxOptions
          static
          // modal={false}: ComboboxOptions defaults to modal, which runs
          // useInertOthers + a scroll lock. Both are already owned by the
          // Dialog around it, and the nested copy marks every sibling that
          // holds no combobox element `inert` + `aria-hidden` — which is the
          // retry panel below.
          modal={false}
          aria-busy={pending}
          className="max-h-80 overflow-y-auto py-2"
        >
          <ComboboxOption
            value={`${langPath(lang, "/search")}?q=${encodeURIComponent(query)}`}
            className="data-[focus]:bg-muted flex cursor-pointer items-center gap-3 px-5 py-2.5 text-sm"
          >
            <Search className="text-mid-gray size-4 shrink-0" aria-hidden />
            <span className="truncate">
              {t.search.seeAll}: <span className="font-semibold">“{query}”</span>
            </span>
          </ComboboxOption>

          {hits.map((h) => (
            <ComboboxOption
              // Module and news ids come from different tables and can collide.
              key={`${h.kind}-${h.id}`}
              value={h.href}
              className="data-[focus]:bg-muted flex cursor-pointer items-center gap-3 px-5 py-2.5 text-sm"
            >
              <span className="text-mid-gray border-border w-20 shrink-0 truncate rounded-pill border px-2 py-0.5 text-center text-xs">
                {h.kind === "news" ? t.nav.news : (typeLabels[h.refType] ?? h.refType)}
              </span>
              <span className="truncate font-semibold">{h.name}</span>
            </ComboboxOption>
          ))}

          {query.length >= 2 && !pending && !failed && hits.length === 0 ? (
            <p className="text-mid-gray px-5 py-2.5 text-sm">{t.search.empty}</p>
          ) : null}

          <ComboboxOption
            value={isAuthed ? askHref : langPath(lang, "/register")}
            className="data-[focus]:bg-accent/10 border-border mt-2 flex cursor-pointer items-center gap-3 border-t px-5 pt-3 pb-2.5 text-sm"
          >
            <Sparkles className="text-accent size-4 shrink-0" aria-hidden />
            <span className="truncate">
              {isAuthed ? t.search.askAssistant : t.search.aiPitchCta}:{" "}
              <span className="font-semibold">“{query}”</span>
            </span>
          </ComboboxOption>
        </ComboboxOptions>
      ) : (
        <p className="text-mid-gray px-5 py-4 text-sm">{t.search.prompt}</p>
      )}
      {/* The live region is mounted ALWAYS, not with its text: a role="status"
          inserted already-populated counts as initial content and NVDA/VoiceOver
          skip it, so the failure would never be announced. `empty:sr-only`
          keeps the empty case out of the layout without leaving the
          accessibility tree (display:none would drop the region entirely). */}
      <div className={failed ? "border-border border-t px-5 py-3 text-sm" : "px-5"}>
        <p role="status" className="text-muted-foreground empty:sr-only">
          {failed ? t.search.suggestionsUnavailable : ""}
        </p>
        {failed ? (
          <Button
            variant="ghost"
            // Keep focus in the input: HeadlessUI's ComboboxInput closes the
            // combobox on blur whose relatedTarget is outside the options or
            // the button, and its focus handler only reopens when `immediate`
            // is set (it is not) — so a plain click here would leave Enter
            // inert while the "see all results" row is still on screen.
            onMouseDown={(e) => e.preventDefault()}
            onClick={retry}
            className="mt-2"
          >
            {t.errors.tryAgain}
          </Button>
        ) : null}
      </div>
      {/* Keyboard hints — symbols only (language-neutral, no Dict keys), and
          desktop-only: they describe keys a phone doesn't have. */}
      <div className="border-border bg-muted/30 text-mid-gray hidden items-center justify-end gap-3 border-t px-5 py-2 text-[11px] md:flex">
        <kbd className={KBD}>↑↓</kbd>
        <kbd className={KBD}>↵</kbd>
        <kbd className={KBD}>Esc</kbd>
      </div>
    </Combobox>
  );
}

const KBD =
  "rounded-md border border-border bg-card px-1.5 py-0.5 font-sans font-semibold shadow-[0_1px_0_var(--color-border)]";
